import { useCallback, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styled from 'styled-components';

import { Checkbox } from '@components/checkbox';
import { IconCheckboxChecked } from '@components/icons/checkbox-checked';
import { IconCheckboxIdle } from '@components/icons/checkbox-idle';
import { IconDrag } from '@components/icons/drag';
import { IconSettings } from '@components/icons/settings';
import { PopoverButton } from '@components/popover-button';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { reorderList } from '@utils/collections';

import type {
  Column,
  ColumnSetting,
  ColumnSettings,
  ColumnSettingsChange,
  Columns,
} from './data-grid-types';

export interface Props<T> {
  columns: Array<Column<T> & ColumnSetting>;
  settings: ColumnSettings<T>;
  onChange: ColumnSettingsChange<T>;
}

export function DataGridSettings<T = unknown>(props: Props<T>) {
  const { columns, onChange, settings } = props;
  const [draggingKey, setDraggingKey] = useState('');

  const sensors = useSensors(useSensor(PointerSensor));
  const sortableItems = columns.map((column) => column.key) as Array<string>;

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!event.active.data.current || !event.over?.data.current) return;

      const from = event.active.data.current.sortable.index;
      const to = event.over.data.current.sortable.index;
      onChange(
        reorderList(columns, from, to).reduce((obj, col, index) => {
          obj[col.key] = {
            visible: col.visible,
            order: index,
          };
          return obj;
        }, {} as ColumnSettings<T>)
      );
    },
    [columns, onChange]
  );

  function toggleColumn(columnKey: Columns<T>[number]['key']) {
    const newSettings = {
      ...settings,
      [columnKey]: {
        ...settings[columnKey],
        visible: !settings[columnKey]?.visible,
      },
    };

    // Make sure the user can't hide ALL column
    const visibleColumns = Object.keys(newSettings).filter((key) => newSettings[key].visible);
    if (visibleColumns.length < 1) return;

    onChange(newSettings);
  }

  return (
    <PopoverButton
      placement="bottom-end"
      triggerProps={{
        iconLeft: <IconSettings />,
        variant: 'ghost',
      }}
    >
      <DndContext
        sensors={sensors}
        onDragStart={(event) => {
          setDraggingKey(event.active.id.toString());
        }}
        onDragCancel={() => setDraggingKey('')}
        onDragEnd={onDragEnd}
      >
        <DragOverlay>
          <OverlayPanelItem column={columns.find((col) => col.key === draggingKey)!} />
        </DragOverlay>
        <SortableContext items={sortableItems}>
          <div
            onMouseDown={(e) => {
              e.preventDefault();
            }}
          >
            {columns.map((column) => (
              <DraggablePanelItem
                column={column}
                key={column.key as string}
                toggle={() => toggleColumn(column.key)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </PopoverButton>
  );
}

type PanelItemProps<T> = {
  column: Column<T> & ColumnSetting;
  toggle(): void;
};

function OverlayPanelItem<T>({ column }: Pick<PanelItemProps<T>, 'column'>) {
  return (
    <Item $draggingOver={false} $dragging>
      {column.visible ? (
        <IconCheckboxChecked color="interactiveElementSelected" />
      ) : (
        <IconCheckboxIdle color="interactiveElementNeutral" />
      )}
      <Text ellipsis text={column.label} font="regular14" />
      <DragButton>
        <IconDrag />
      </DragButton>
    </Item>
  );
}

function DraggablePanelItem<T>({ column, toggle }: PanelItemProps<T>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, active } =
    useSortable({
      id: String(column.key),
    });

  return (
    <Item
      $draggingOver={false}
      $dragging={false}
      ref={setNodeRef}
      style={{
        zIndex: isDragging ? 2 : 1,
        opacity: isDragging ? 0.2 : active?.id === String(column.key) ? 0.8 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Checkbox
        onChange={toggle}
        label={column.label}
        labelVisible={false}
        value={column.visible}
      />
      <Text ellipsis font="regular14">
        <span onClick={toggle}>{column.label}</span>
      </Text>
      <DragButton {...attributes} {...listeners}>
        <IconDrag />
      </DragButton>
    </Item>
  );
}

const DragButton = styled.div({
  backgroundColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1.5rem',
  height: '1.5rem',
  flexShrink: 0,
  border: 'none',
  borderRadius: '0.25rem',
  padding: 0,
  margin: 0,
  color: theme.color.buttonIconPositive,
  outline: 'none',

  ':focus-visible': {
    color: theme.color.purple80,
    boxShadow: theme.shadow.focus,
    '--shadow-focus-color': theme.color.purple100,
    '--shadow-focus-gap': 0,
    '--shadow-focus-width': '0.1rem',
  },

  ':hover': {
    color: theme.color.buttonIconPositiveHover,
    backgroundColor: theme.color.uiTrans10,
  },
});

const Item = styled.div<{ $draggingOver: boolean; $dragging: boolean }>((p) => ({
  position: 'relative',
  backgroundColor: theme.color.backgroundLightDark,
  height: theme.base.height,
  display: 'flex',
  alignItems: 'center',
  padding: '0 0.5rem',
  width: '100%',
  font: theme.font.regular14,
  color: theme.color.textPrimary,
  borderRadius: '0.25rem',
  outline: 'none',
  overflow: 'hidden',
  cursor: 'pointer',

  ...(!p.$dragging && {
    ':hover': {
      backgroundColor: theme.color.uiTrans5,
    },
  }),

  // Checkbox label
  '> span': {
    display: 'block',
    paddingLeft: '0.5rem',
    flexGrow: 1,
  },

  ...(p.$dragging && { boxShadow: theme.shadow.high }),

  // Drag handle
  button: {
    cursor: 'grab',
  },
}));
